/**
 * OAuthAutorizarView -- QUIEN PIDE ENTRAR AL MCP DE NEXO, Y TU DECIDES (MCP-NEXO-OAUTH-T1, PR c; D5 de Ares #23523).
 *
 * AUDIENCIA: Josse, con su sesion de admin del panel. Llega aqui por un enlace que arma la API cuando ChatGPT (u otro
 * cliente registrado) pide permiso: #/oauth/autorizar?solicitud=<uuid>. Casi siempre en el computador, a veces en el
 * celular: por eso una sola columna.
 *
 * QUE DEBE PODER RECONOCER ANTES DE APROBAR (Q en el PR 448, #23550): el redirect de ChatGPT es el mismo para cualquier
 * conector, asi que una solicitud que Josse NO inicio llega igual a su panel. La pantalla le muestra el cliente, su
 * client_id, cuando se registro y hace cuanto se pidio esto; con eso decide si es suya.
 *
 * APROBAR o NEGAR: los dos devuelven un enlace de vuelta a ChatGPT (con el codigo, o con access_denied). El panel solo
 * lo sigue si cumple la misma regla de la API (https, chatgpt.com o chat.openai.com): si no, lo muestra y no navega.
 */
import { AppConfig } from '../../config.js';
import { OAuthApiClient, solicitudDelHash } from '../../infrastructure/api/OAuthApiClient.js';
import { SCOPES, ContextApiClient } from '../../infrastructure/api/ContextApiClient.js';
import { candidatasOAuth, fraseDeEntrada, etiquetaDeLlave, avisoDeEleccion, vistaPreviaDeLaElegida } from '../../application/contexto/llaveOAuth.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';

const C = { texto: '#e8e8f0', suave: '#8a8aa8', linea: '#2a2a3e', bien: 'var(--accent-green-text)', ojo: 'var(--accent-yellow)', mal: '#ff9a9a', acento: '#7fe8d8' };
// Nadie queda encerrado en una pantalla nueva (pedido de Josse del 4-sep; lo exige el build).
const VOLVER = regresoHtml();
// Deuda 4: el escape local no cambiaba la comilla simple; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';

/** "hace 3 min", "hace 2 dias"; '' si no hay fecha. */
export function hace(v, ahora = Date.now()) {
  const t = v ? new Date(v).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.round((ahora - t) / 1000));
  if (s < 90) return 'hace ' + s + ' s';
  const m = Math.round(s / 60);
  if (m < 90) return 'hace ' + m + ' min';
  const h = Math.round(m / 60);
  if (h < 36) return 'hace ' + h + ' h';
  return 'hace ' + Math.round(h / 24) + ' dias';
}

/** "en 4 min" o "vencida". */
function vence(v, ahora = Date.now()) {
  const t = v ? new Date(v).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const m = Math.round((t - ahora) / 60000);
  return m <= 0 ? 'vencida' : 'vence en ' + m + ' min';
}

function permiso(id) {
  const s = SCOPES.find((x) => x.id === id);
  return '<li style="margin:4px 0"><span style="color:' + C.texto + '">' + esc(s ? s.que : id) + '</span>'
    + '<br><span style="color:' + C.suave + ';font-size:0.875rem">' + esc(id) + (s ? ' &middot; riesgo ' + esc(s.riesgo) : ' &middot; permiso que este panel no conoce') + '</span></li>';
}

function dato(etiqueta, valor, falta) {
  return '<div style="display:flex;justify-content:space-between;gap:12px;padding:5px 0;border-bottom:1px solid ' + C.linea + ';font-size:0.875rem">'
    + '<span style="color:' + C.suave + '">' + esc(etiqueta) + '</span>'
    + '<span style="color:' + (valor ? C.texto : C.ojo) + ';text-align:right;overflow-wrap:anywhere">' + esc(valor || falta) + '</span></div>';
}

/** Lo que podra hacer con la llave PROPUESTA: la API ya lo calculo (scopesQueRecibiria, con tu consentimiento). */
function podraHtml(s) {
  return '<p style="font-size:0.875rem;color:' + C.suave + ';margin:12px 0 4px">Si apruebas, podra:</p>'
    + (!s.recibiria.length
      ? '<p style="font-size:0.875rem;color:' + C.ojo + '">Con la llave y tu consentimiento de hoy no tendria ningun permiso. Revisalo en <a href="#/contexto" style="color:' + C.acento + '">Accesos de asistentes</a>.</p>'
      : '<ul style="margin:0;padding-left:18px;font-size:0.875rem">' + s.recibiria.map(permiso).join('') + '</ul>')
    + (s.pedidos.some((p) => !s.recibiria.includes(p))
      ? '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:6px">Pidio ademas ' + esc(s.pedidos.filter((p) => !s.recibiria.includes(p)).join(', ')) + ', que no recibira: tu llave o tu consentimiento no los dan.</p>' : '');
}

/** Con OTRA llave la API recalcula al aprobar (pedido, llave y consentimiento): aqui se dice lo que la llave tiene, y eso. */
function podraConOtraHtml(c) {
  return '<p style="font-size:0.875rem;color:' + C.suave + ';margin:12px 0 4px">La llave ' + esc(c.agente) + ' tiene estos permisos:</p>'
    + '<ul style="margin:0;padding-left:18px;font-size:0.875rem">' + c.permisos.map(permiso).join('') + '</ul>'
    + '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:6px">Al aprobar, la API le da los que el cliente pidio y tu consentimiento permite.</p>';
}

// Ares #27303: cada opcion dice si es la PROPUESTA, cuantos permisos tiene y de que negocio es (en letra, no solo el prefijo).
function opcionHtml(c, marcada, propuestaId, negocioSesion) {
  const e = etiquetaDeLlave(c, propuestaId, negocioSesion);
  return '<label style="display:flex;gap:10px;align-items:flex-start;padding:8px 10px;border:1px solid ' + (marcada ? C.acento : C.linea) + ';border-radius:8px;margin-top:6px;cursor:pointer" data-oauth-opcion="' + esc(c.id) + '">'
    + '<input type="radio" name="oauth-llave" value="' + esc(c.id) + '"' + (marcada ? ' checked' : '') + ' style="margin-top:3px">'
    + '<span style="font-size:0.875rem;color:' + C.texto + ';overflow-wrap:anywhere"><strong>' + esc(c.agente) + '</strong>' + (c.nombre ? ' &middot; ' + esc(c.nombre) : '')
    + (e.propuesta ? ' <span data-oauth-propuesta style="font-size:0.875rem;color:var(--accent-green-text);border:1px solid #3fb95066;border-radius:6px;padding:0 6px;white-space:nowrap">la que propone la API</span>' : '')
    + '<br><span data-oauth-detalle style="font-size:0.875rem;color:' + C.texto + '"><strong>' + esc(e.textoPermisos) + '</strong> &middot; ' + esc(e.negocio) + '</span>'
    + '<br><span style="font-family:monospace;font-size:0.875rem;color:' + C.suave + '">' + esc(c.prefijo) + '&hellip;</span></span></label>';
}

export async function renderOAuthAutorizar(container) {
  const $el = document.createElement('section');
  $el.className = 'detail-section';
  $el.setAttribute('data-oauth-autorizar', '');
  $el.style.cssText = 'max-width:560px;margin:0 auto';
  container.appendChild($el);

  // La decision es de la sesion de admin (la API lo exige igual con RequireAdmin): sin ella, ni se pregunta.
  if (!['admin', 'dev'].includes(AppConfig.role)) {
    $el.innerHTML = VOLVER + '<h1 style="font-size:1.05rem;color:' + C.texto + ';margin:0">&#128274; Autorizar un acceso al MCP</h1>'
      + '<p data-oauth-aviso style="font-size:0.875rem;color:' + C.ojo + '">Esta autorizacion es de la sesion de admin de Josse. '
      + 'Si eres Josse, entra con tu usuario de admin y vuelve a abrir el enlace.</p>';
    return;
  }

  const id = solicitudDelHash(window.location.hash);
  $el.innerHTML = VOLVER + '<h1 style="font-size:1.05rem;color:' + C.texto + ';margin:0">&#128274; Autorizar un acceso al MCP de Nexo</h1>'
    + '<div data-oauth-cuerpo style="margin-top:12px;color:' + C.suave + ';font-size:0.875rem">Buscando la solicitud&hellip;</div>'
    + '<div data-oauth-aviso role="status" style="font-size:0.875rem;min-height:18px;margin-top:10px"></div>';
  const $cuerpo = $el.querySelector('[data-oauth-cuerpo]');
  const $aviso = $el.querySelector('[data-oauth-aviso]');
  const decir = (t, color) => { $aviso.style.color = color || C.suave; $aviso.textContent = t; };

  const s = await OAuthApiClient.leerSolicitud(id);
  if (!s.ok) {
    $cuerpo.innerHTML = '<p style="color:' + C.mal + '">' + esc(s.detalle) + '</p>';
    return;
  }

  const sinPermisos = !s.recibiria.length;
  $cuerpo.innerHTML = ''
    + '<p style="font-size:.9rem;color:' + C.texto + ';margin:0 0 10px"><strong>' + esc(s.cliente || 'Un cliente') + '</strong> pide entrar al MCP de Nexo en tu nombre.</p>'
    + '<div style="background:var(--bg-secondary);border:1px solid ' + C.linea + ';border-radius:10px;padding:8px 12px">'
    + dato('Cliente', s.cliente, '(sin nombre)')
    + dato('client_id', s.clienteId, 'la API aun no lo envia (PR b)')
    + dato('Se registro', s.clienteRegistrado ? hace(s.clienteRegistrado) : '', 'la API aun no lo envia (PR b)')
    + dato('Lo pidio', s.creada ? hace(s.creada) : '', 'la API aun no lo envia (PR b)')
    + dato('Solicitud', s.vence ? vence(s.vence) : '', '')
    + '</div>'
    // OAUTH-ELEGIR-LLAVE-T1: con cual llave entra. Se llena despues con las llaves vivas de la persona de la propuesta.
    + '<div data-oauth-llaves style="margin-top:12px">' + dato('Entraria como', s.agente, '(sin agente)') + '</div>'
    + '<div data-oauth-podra>' + podraHtml(s) + '</div>'
    + '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:10px">Si no reconoces esta solicitud (no la pediste tu ahora), niegala.</p>'
    + (s.decidida === true
      ?'<p style="font-size:0.875rem;color:' + C.ojo + '">Esta solicitud ya se decidio.</p>'
      : '<div style="display:flex;gap:10px;margin-top:14px;flex-wrap:wrap">'
        + '<button type="button" data-oauth-negar style="flex:1 1 140px;min-height:44px;border-radius:10px;border:1px solid ' + C.linea + ';background:var(--bg-tertiary);color:' + C.texto + ';font-size:.9rem;cursor:pointer">Negar</button>'
        + '<button type="button" data-oauth-aprobar ' + (sinPermisos ? 'disabled ' : '') + 'style="flex:1 1 140px;min-height:44px;border-radius:10px;border:0;background:var(--accent-green);color:var(--text-on-accent);font-size:.9rem;font-weight:600;cursor:pointer">Aprobar</button>'
        + '</div>');

  // OAUTH-ELEGIR-LLAVE-T1: las llaves vivas de la persona de la propuesta, la propuesta marcada. Si no se pueden listar,
  // queda la fila de antes ("Entraria como") y se aprueba con la propuesta, como hasta hoy.
  let elegida = '';
  let sinPermisosAhora = sinPermisos;
  const $llaves = $el.querySelector('[data-oauth-llaves]');
  const $podra = $el.querySelector('[data-oauth-podra]');
  const $aprobar = () => $el.querySelector('[data-oauth-aprobar]');
  if (s.decidida !== true && s.credencialPropuestaId) {
    const rc = await ContextApiClient.listCredentials();
    const cand = rc.ok ? candidatasOAuth(rc.credenciales, s.credencialPropuestaId) : null;
    if (cand && cand.lista.length) {
      elegida = cand.elegida;
      const pintarLlaves = () => {
        const c = cand.lista.find((x) => x.id === elegida);
        const negocioSesion = { id: getSelectedBusiness() || '', nombre: (() => { try { return getSelectedBusinessName(); } catch { return ''; } })() };
        const aviso = avisoDeEleccion(c, cand.lista.find((x) => x.id === s.credencialPropuestaId));
        $llaves.innerHTML = '<p style="font-size:0.875rem;color:' + C.suave + ';margin:0 0 2px">Con cual llave entra'
          + (cand.lista.length > 1 ? ' (' + cand.lista.length + ' vivas de esta persona)' : '') + ':</p>'
          + cand.lista.map((x) => opcionHtml(x, x.id === elegida, s.credencialPropuestaId, negocioSesion)).join('')
          + '<p data-oauth-entrada style="font-size:0.875rem;color:' + C.texto + ';margin:8px 0 0"><strong>' + esc(fraseDeEntrada(c)) + '</strong></p>'
          + (aviso ? '<p data-oauth-aviso-menos role="alert" style="font-size:0.875rem;color:' + C.ojo + ';margin:6px 0 0">&#9888;&#65039; ' + esc(aviso) + '</p>' : '');
        const propia = elegida === s.credencialPropuestaId;
        $podra.innerHTML = propia ? podraHtml(s) : podraConOtraHtml(c);
        sinPermisosAhora = propia ? sinPermisos : !c.permisos.length;
        const $b = $aprobar();
        if ($b) $b.disabled = sinPermisosAhora;
        if (!propia) recalcular(elegida);
      };
      // #42497: con otra llave se pide la vista previa a la API (?credencial=) para decir lo que RECIBIRIA (pedido, llave
      // y consentimiento), no solo lo que la llave tiene. Gana la ultima eleccion; si la API no la recalcula, queda lo de arriba.
      let pedida = 0;
      const recalcular = async (llave) => {
        const turno = ++pedida;
        const r = await OAuthApiClient.leerSolicitud(s.id, llave);
        if (turno !== pedida || llave !== elegida) return;
        const v = vistaPreviaDeLaElegida(r, llave);
        if (!v) {
          if (r.motivo === 'LLAVE_AJENA') $podra.insertAdjacentHTML('beforeend', '<p data-oauth-llave-ajena style="font-size:0.875rem;color:' + C.mal + ';margin-top:6px">' + esc(r.detalle) + '.</p>');
          return;
        }
        $podra.innerHTML = '<div data-oauth-recalculada>' + podraHtml(v) + '</div>';
        sinPermisosAhora = !v.recibiria.length;
        const $b = $aprobar();
        if ($b) $b.disabled = sinPermisosAhora;
      };
      pintarLlaves();
      $llaves.addEventListener('change', (e) => {
        if (e.target?.name !== 'oauth-llave') return;
        elegida = e.target.value;
        pintarLlaves();
      });
    } else if (!rc.ok) {
      $llaves.insertAdjacentHTML('beforeend', '<p style="font-size:0.875rem;color:' + C.suave + ';margin:4px 0 0">No pude listar las otras llaves ('
        + esc(rc.detalle) + '): si apruebas, entra con la propuesta' + (s.credencialPropuestaPrefijo ? ' (' + esc(s.credencialPropuestaPrefijo) + ')' : '') + '.</p>');
    }
  }

  const decidir = async (aprobar, $b) => {
    $el.querySelectorAll('[data-oauth-aprobar],[data-oauth-negar]').forEach((b) => { b.disabled = true; });
    $llaves?.querySelectorAll('input[name="oauth-llave"]').forEach((i) => { i.disabled = true; });
    decir(aprobar ? 'Aprobando...' : 'Negando...');
    // La llave elegida viaja SOLO al aprobar; negar no ata ninguna.
    const r = await OAuthApiClient.decidir(s.id, aprobar, aprobar ? elegida : null);
    if (!r.ok) {
      decir(r.detalle, C.mal);
      $el.querySelectorAll('[data-oauth-aprobar],[data-oauth-negar]').forEach((b) => { b.disabled = b.hasAttribute('data-oauth-aprobar') && sinPermisosAhora; });
      $llaves?.querySelectorAll('input[name="oauth-llave"]').forEach((i) => { i.disabled = false; });
      return;
    }
    if (!r.seguro) {
      // Defensa en profundidad: la API ya limita a chatgpt.com; si algun dia devolviera otra cosa, no se sigue.
      decir('La API devolvio un enlace que no es de ChatGPT y no lo sigo: ' + r.redirect, C.mal);
      return;
    }
    decir(aprobar ? 'Aprobado. Volviendo a ChatGPT...' : 'Negado. Volviendo a ChatGPT...', aprobar ? C.bien : C.suave);
    window.location.assign(r.redirect);
  };
  $el.querySelector('[data-oauth-aprobar]')?.addEventListener('click', (e) => decidir(true, e.currentTarget));
  $el.querySelector('[data-oauth-negar]')?.addEventListener('click', (e) => decidir(false, e.currentTarget));
}
